import '../CommonCommericalSurvey/CustomExpression';
import '../CommonCommericalSurvey/CustomProperties';
import '../CommonCommericalSurvey/CustomWidgets';
import '../CommonCommericalSurvey/CommercialSurvey.css';

import { useApolloClient } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';

import Loading from '../../components/Loading/Loading';
import Navigation from '../../components/Navigation/Navigation';
import {
  ApplicationResultKind,
  GetThirdJsonDocument,
  GetThirdJsonQuery,
  GetThirdJsonQueryVariables,
  useCompleteApplicationMutation
} from '../../generated/graphql';
import { formatDateAnswers } from '../../utils/SurveyJSQuestion';
import { SurveyCSS } from '../CommonCommericalSurvey/commonVariables';

interface LobShellState {
  phase: 3;
  fetchThirdJSONs: string[];
  carrierPartner: string;
  phase2LastPageName: string;
  /**
   * Filename of the Phase 2 (biz_coverage / pre-split combined) JSON the user
   * just left. Forwarded as `jsonName` to the fallback completeApplication
   * call when Phase 3 JSON fails to load. Optional for back-compat with
   * navigation state from older frontend builds.
   */
  phase2JsonFileName?: string;
  country: string;
  province: string;
}

const LobShell: React.FC = () => {
  const location = useLocation();
  const { applicationId } = useParams<{ applicationId: string }>();
  const apolloClient = useApolloClient();
  const {
    fetchThirdJSONs,
    carrierPartner,
    phase2LastPageName,
    phase2JsonFileName,
    country,
    province
  } = location.state as LobShellState;

  interface QueryState {
    thirdJson: Record<string, unknown> | null;
    previousAnswers: Record<string, unknown> | null;
    phase2Answers: Record<string, unknown>;
    isLoading: boolean;
  }
  const [queryState, setQueryState] = useState<QueryState>({
    thirdJson: null,
    previousAnswers: null,
    phase2Answers: {},
    isLoading: true
  });
  const {
    thirdJson,
    previousAnswers,
    phase2Answers,
    isLoading: isLoadingJson
  } = queryState;

  // Fetch Phase 3 JSON on mount — moved here from CommercialSurveyWithApplicationId
  useEffect(() => {
    apolloClient
      .query<GetThirdJsonQuery, GetThirdJsonQueryVariables>({
        query: GetThirdJsonDocument,
        variables: { applicationId, thirdJsonFileName: fetchThirdJSONs[0] },
        fetchPolicy: 'network-only'
      })
      .then(({ data }) => {
        const response = data?.getThirdJson ?? null;
        const json = response?.json ?? null;
        const prevAnswers = response?.previousAnswers ?? null;
        const secondJsonAnswers =
          (response?.answersFromSecondJson as Record<string, unknown> | null) ??
          {};
        setQueryState({
          thirdJson: json,
          previousAnswers: prevAnswers,
          phase2Answers: secondJsonAnswers,
          isLoading: false
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reason Flag is part of Phase 2's stored answers — derive it instead of
  // threading it through router state.
  const phase2ReasonFlag = phase2Answers['Reason Flag'] as string | undefined;

  const model = useMemo(() => {
    if (!thirdJson) return null;
    const m = new Model(thirdJson);
    m.setVariable('carrierPartner', carrierPartner);
    // Inject flags as variables — they affect visibleIf but are NOT included in model.data,
    // so they won't be sent to completeApplication (backend rejects answers containing flag fields)
    m.setVariable(
      'BusinessInformation_MunichProduct',
      phase2Answers['BusinessInformation_MunichProduct']
    );
    m.setVariable(
      'BusinessInformation_Broker',
      phase2Answers['BusinessInformation_Broker']
    );

    // Endorsement/renewal: pre-fill Munich page answers from the prior policy.
    // Same pattern as CommercialWithApplicationId for Phase 2 — strip first-page
    // fields (set fresh on the new application) and inject the rest. Phase 2
    // answers are applied afterwards so current-application values win.
    if (previousAnswers) {
      const {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        BusinessInformation_100_EffectiveDate_WORLD_EN: _effectiveDate,
        BusinessInformation_100_Profession_WORLD_EN: _profession,
        BusinessInformation_100_CustomerInfo_WORLD_EN: _customerInfo,
        BusinessInformation_100_State_WORLD_EN: _state,
        /* eslint-enable @typescript-eslint/no-unused-vars */
        ...previousAnswersWithoutFirstPage
      } = previousAnswers;
      m.setVariable('isEndorsement', true);
      m.setDataCore({ ...m.data, ...previousAnswersWithoutFirstPage });
    }

    // Strip flags that must not reach completeApplication (backend rejects them).
    // Munich_9219_QuoteFlag is NOT stripped here — it is injected via phase2AnswersWithoutFlags
    // so Phase 3 triggers start with the Phase 2-computed value. The Phase 3 JSON defines it
    // as visible:false, so clearInvisibleValues:"onComplete" removes it from model.data
    // automatically before completeApplication is called.
    const {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      BusinessInformation_MunichProduct: _munichProduct,
      BusinessInformation_Broker: _broker,
      Beazley_5415_QuoteFlag: _beazleyFlag,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      ...phase2AnswersWithoutFlags
    } = phase2Answers;
    // Use setDataCore + runTriggers (not direct m.data assignment) so that
    // runexpression triggers fire — in particular the workExclusionVariableText
    // trigger that computes Munich_PersonalServices_9219_WorkExclusionListHtml_WORLD_EN.
    m.setDataCore({ ...m.data, ...phase2AnswersWithoutFlags });
    m.runTriggers();

    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thirdJson]);

  const [isComplete, setComplete] = useState(false);
  const [isRedirectToQuote, setRedirectToQuote] = useState(false);
  // Captured from the completeApplication response so the final redirect
  // goes to `/quote/<applicationGroupId>` per master's PR #1153 route rename.
  const [
    completedApplicationGroupId,
    setCompletedApplicationGroupId
  ] = useState<string | null>(null);

  const [
    completeApplicationMutation,
    { loading: isCompleting }
  ] = useCompleteApplicationMutation();

  if (isLoadingJson || isCompleting) return <Loading />;

  if (isComplete) {
    if (isRedirectToQuote) {
      if (!completedApplicationGroupId) {
        throw new Error(
          'applicationGroupId missing from completeApplication response'
        );
      }
      return (
        <Redirect to={{ pathname: `/quote/${completedApplicationGroupId}` }} />
      );
    }
    return (
      <Redirect to={{ pathname: '/complete', state: { country, province } }} />
    );
  }

  // Fallback: getThirdJson returned null (edge case — e.g. sendToBrokerFlag toggled between
  // Phase 2 submit and Phase 3 load). Call completeApplication directly.
  if (!thirdJson || !model) {
    const {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      BusinessInformation_MunichProduct: _munichProduct,
      Munich_9219_QuoteFlag: _munichFlag,
      BusinessInformation_Broker: _broker,
      Beazley_5415_QuoteFlag: _beazleyFlag,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      ...answersWithoutFlags
    } = phase2Answers;

    completeApplicationMutation({
      variables: {
        answersInfo: {
          applicationId,
          answers: answersWithoutFlags,
          isBrokerFlow: !!(
            phase2Answers['BusinessInformation_Broker'] ||
            !phase2Answers['Munich_9219_QuoteFlag']
          ),
          reasonFlag: phase2ReasonFlag
        },
        pageName: phase2LastPageName,
        // Phase 3 JSON failed to load — we're completing from the Phase 2
        // answer state, so the row's source JSON is the biz_coverage filename.
        jsonName: phase2JsonFileName
      }
    }).then(({ data: result }) => {
      if (!result) throw new Error('completeApplicationData should be ready');
      const {
        errors,
        kind,
        applicationGroupId: resultApplicationGroupId
      } = result.completeApplication;
      if (errors.length > 0) throw new Error(errors.join(' - '));
      setCompletedApplicationGroupId(resultApplicationGroupId);
      if (kind === ApplicationResultKind.OfferQuote) {
        setRedirectToQuote(true);
      }
      setComplete(true);
    });

    return <Loading />;
  }

  // Munich path: show the LOB survey page
  const doComplete = async () => {
    // Capture fields before completeLastPage() — SurveyJS's default
    // clearInvisibleValues:"onComplete" removes invisible questions from model.data.
    const preCompleteMunichFlag = model.data['Munich_9219_QuoteFlag'];
    const preCompleteReasonFlag =
      (model.data['Reason Flag'] as string | undefined) || phase2ReasonFlag;
    // WorkExclusionListHtml is visible:false in Phase 3 JSON → cleared by
    // clearInvisibleValues:"onComplete". Capture before completeLastPage().
    const preCompleteWorkExclusionListHtml =
      model.data['Munich_PersonalServices_9219_WorkExclusionListHtml_WORLD_EN'];
    // WorkExclusionText is an html-type question — html questions don't store values
    // in model.data, so read the html property directly from the question object.
    const workExclusionTextQuestion = model.getQuestionByName(
      'Munich_PersonalServices_9219_WorkExclusionText_WORLD_EN'
    );
    const preCompleteWorkExclusionText = workExclusionTextQuestion
      ? (workExclusionTextQuestion as { html?: string }).html
      : undefined;
    const surveyValidateResult = model.completeLastPage();
    if (!surveyValidateResult) {
      model.currentPage.focusFirstErrorQuestion();
      return;
    }

    const { data: modelData } = model;
    formatDateAnswers(modelData);

    // Strip fetchThirdJSONs — it's a trigger variable (Phase 3 filename), not a user answer
    delete modelData['fetchThirdJSONs'];

    const answersInfo = {
      applicationId,
      answers: {
        ...modelData,
        BusinessInformation_100_BusinessAddress_WORLD_EN:
          phase2Answers.BusinessInformation_100_BusinessAddress_WORLD_EN,
        BusinessInformation_100_CustomerInfo_WORLD_EN:
          phase2Answers.BusinessInformation_100_CustomerInfo_WORLD_EN,
        BusinessInformation_100_EffectiveDate_WORLD_EN:
          phase2Answers.BusinessInformation_100_EffectiveDate_WORLD_EN,
        BusinessInformation_100_CompanyName_WORLD_EN:
          phase2Answers.BusinessInformation_100_CompanyName_WORLD_EN,
        Munich_PersonalServices_9219_WorkExclusionListHtml_WORLD_EN: preCompleteWorkExclusionListHtml,
        Munich_PersonalServices_9219_WorkExclusionText_WORLD_EN: preCompleteWorkExclusionText
      },
      isBrokerFlow: !!(
        phase2Answers['BusinessInformation_Broker'] || !preCompleteMunichFlag
      ),
      reasonFlag: preCompleteReasonFlag
    };

    const { data: result } = await completeApplicationMutation({
      variables: {
        answersInfo,
        pageName: model.currentPage.name,
        // Current page belongs to the Phase 3 LOB JSON — that's the JSON the
        // user just completed and the row should be stamped with.
        jsonName: fetchThirdJSONs[0]
      }
    });

    if (!result) throw new Error('completeApplicationData should be ready');

    const {
      errors,
      kind,
      applicationGroupId: resultApplicationGroupId
    } = result.completeApplication;
    if (errors.length > 0) throw new Error(errors.join(' - '));
    setCompletedApplicationGroupId(resultApplicationGroupId);

    switch (kind) {
      case ApplicationResultKind.OfferQuote:
        setRedirectToQuote(true);
        break;
      case ApplicationResultKind.SendToBroker:
      case ApplicationResultKind.DeclineBusiness:
        setRedirectToQuote(false);
        break;
      default:
        throw new Error('Unreachable - unexpected Application result state');
    }
    setComplete(true);
  };

  return (
    <div className="h-full Commercial" data-testid="LobShell">
      <div className="survey-container">
        <Survey model={model} css={SurveyCSS} />
        <Navigation
          goBack={undefined}
          goNext={undefined}
          doComplete={doComplete}
        />
      </div>
    </div>
  );
};

export default LobShell;
